import { useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  BookingFormType,
  BookingType,
  FormBookingType,
} from '../../../redux/types/bookingTypes';
import SelectComponent from '../../SelectComponent';
import BookingSectionTitle from '../BookingSectionTitle';
import PersonCount from '../PersonCount';
import {
  ADDMONTHS_FROM_TODAY_FOR_CHECKERBOARD,
  COMMENT_REQ,
  SUBMONTHS_FROM_TODAY_FOR_CHECKERBOARD,
} from './../../../utils/constants';
import { subMonths, addMonths, addDays, format, subDays } from 'date-fns';
import {
  createFilteredObjectList,
  statusListOption,
  timeListOption,
} from '../functions';
import Input from '../../Input';

import { InputType, OptionsListType } from '../../../utils/@globalTypes';
import Checkbox from '../../Checkbox';
import Calendar from '../../Calendar';
import BookingLineTitle from '../BookingLineTitle';
import styles from './BookingObjectBlock.module.scss';
import SelectComponentV2 from '../../SelectComponentV2';
import { ANIMALINFO_REQ } from './../../../utils/constants';
import {
  getCurrentRentalObject,
  RentalObjectSelectors,
} from '../../../redux/reducers/rentalObjectSlice';
import { useDispatch, useSelector } from 'react-redux';
import { BookingSelectors, setTotalPrice } from '../../../redux/reducers/bookingSlice';
import { FormikProps, useFormikContext } from 'formik';
import { TariffsSelectors } from '../../../redux/reducers/tariffsSlice';
import { TarifTypes } from '../../../redux/types/@types';
import PriceBlock from '../PriceBlock';

type ObjectblockProps = {
  windowType?: BookingFormType;
  isLast?: boolean;
  formik: FormikProps<FormBookingType>;
  booking?: BookingType;
};
const BookingObjectBlock = ({
  windowType = BookingFormType.view,
  isLast = false,
  formik,
  booking,
}: ObjectblockProps) => {
  const { setFieldValue } = useFormikContext();
  const {
    handleChange,
    values,
    errors,
    setFieldTouched,
    touched,
    validateField,
    initialValues,
    setFieldError,
  } = formik;
  const currentTariff = useSelector(TariffsSelectors.getCurrentTarif);
  const dispatch = useDispatch();
  
  const objects = useSelector(BookingSelectors.getChessObjects);

  const optionList = objects ? createFilteredObjectList(objects) : [];
  const statusListOptionForCreation: OptionsListType =
    currentTariff !== TarifTypes.FREE
      ? statusListOption.filter((item) => item.value !== 'cld')
      : statusListOption.filter(
          (item) => item.value === 'cfm' || item.value === 'awt',
        );

  const [timeList, setTimeList] = useState(timeListOption);
  const current_object = useSelector(
    RentalObjectSelectors.getCurrentRentalObject,
  );

  const max_places = current_object?.max_places || undefined;

  const commentView =
    formik.values.owner_comment.length > 0
      ? formik.values.owner_comment
      : 'Дополнительных комментариев нет';

  
  useEffect(() => {
    if (values.rental_object && objects) {
      dispatch(getCurrentRentalObject(Number(values.rental_object)));
      dispatch(setTotalPrice(null))      
    }
  }, [values.rental_object]);

  useEffect(() => {
    if (
      current_object &&
      values.rental_object &&
      !initialValues.check_out_time
    ) {
      setFieldValue('check_out_time', current_object.booking_setting.check_out);
    }
  }, [current_object]);

  useEffect(() => {
    if (
      current_object &&
      values.rental_object &&
      !initialValues.check_in_time
    ) {
      setFieldValue('check_in_time', current_object.booking_setting.check_in);
    }
  }, [current_object]);

  const animalInfoView = !values.has_animals ? 'Нет' : values.animals_info;
  const today = new Date();
  const startCalendar = subMonths(
    new Date(),
    SUBMONTHS_FROM_TODAY_FOR_CHECKERBOARD,
  );
  const endCalendar = addMonths(
    new Date(),
    ADDMONTHS_FROM_TODAY_FOR_CHECKERBOARD,
  );
  const endCalendarForCheckIn = subDays(endCalendar, 1);

  useEffect(() => {
    if (touched.rental_object) {
      validateField('rental_object');
    }
  }, [touched, values]);

  useEffect(() => {
    if (max_places) {
      let totalGuests = values.adult;  
      if(values.children){      
      for(let i=0; i<values.children?.length; i++){
        if(values.children[i]>=2){ 
        totalGuests= totalGuests+1
        }}
      }         
      if (totalGuests > max_places) {
        setFieldError('adult', 'Превышена максимальная вместимость объекта');
      } else {
        setFieldError('adult', undefined);
      }
    }
  }, [max_places, values]);

  useEffect(() => {
    if (touched.status) {
      validateField('status');
    }
  }, [touched, values]);

  useEffect(() => {   
    validateField('animals_count');
  },[values.has_animals]);

  useEffect(() => {
    if (touched.animals_info) {
      validateField('animals_info');
    }
    if (touched.animals_count) {
      validateField('animals_count');
    }
  }, [touched, values]);

  useEffect(() => {
    if (!values.has_animals && values.animals_info.length > 0) {
      setFieldValue('animals_info', '');
      validateField('animals_info');
    }
  }, [touched, values]);

  useEffect(() => {
    if (!values.has_animals && Number(values.animals_count) > 0) {
      setFieldValue('animals_count', '');
      validateField('animals_count');
    }
  }, [touched, values]);

  useEffect(() => {
    // if (touched.check_in_date || touched.check_out_date) {
      validateField('check_in_date');
      validateField('check_out_date');
    // }
  }, [touched, values]);

  useEffect(() => {
    if (values.check_in_time && values.check_out_time) {
      validateField('check_in_time');
      validateField('check_out_time');
    }
  }, [
    values.check_in_date,
    values.check_out_date,
    values.check_in_time,
    values.check_out_time,
  ]);

  useEffect(() => {
    if (values.check_in_date && values.check_in_date > values.check_out_date) {
      setFieldValue('check_out_date', addDays(values.check_in_date, 1));
    }
  }, [touched, values]);

  const createdDate =
    booking?.created_at && format(new Date(booking?.created_at), 'dd.MM.yyyy');

  return (
    <div className={styles.wrap}>
      <BookingSectionTitle
        title="Информация об объекте"
        created={createdDate}
      />
      <div className={styles.container}>
        <div className={classNames(styles.line, styles.firstLine)}>
          <div className={styles.column}>
            <SelectComponent
              name="rental_object"
              id="rental_object"
              title={windowType === BookingFormType.view ? 'Объект' : 'Объект*'}
              optionsList={optionList}
              currentValue={
                (values.rental_object && values.rental_object.toString()) || ''
              }
              // isSearchable={windowType===BookingFormType.view ? false : true}
              isDisabled={
                windowType === BookingFormType.view || isLast ? true : false
              }
              setSelecValue={handleChange('rental_object')}
              isBooking={true}
              onBlur={() => setFieldTouched('rental_object')}
              errText={errors.rental_object}
            />
          </div>
          <div className={styles.column}>
            <SelectComponent
              name="status"
              id="status"
              title={isLast ? 'Статус' : 'Статус*'}
              optionsList={
                windowType === BookingFormType.new
                  ? statusListOptionForCreation
                  : statusListOption
              }
              currentValue={values.status}
              setSelecValue={handleChange('status')}
              isBooking={true}
              onBlur={() => setFieldTouched('status')}
              defaultValueId={
                windowType === BookingFormType.new ? 0 : undefined
              }
              isDisabled={windowType === BookingFormType.view || isLast}
              errText={errors.status}
            />
          </div>
        </div>

        <div className={classNames(styles.line, styles.secondLine)}>
          <div
            className={classNames(
              styles.column,
              styles.dateBlock,
              windowType !== BookingFormType.view && styles.edit,
            )}
          >
            <div>
              <BookingLineTitle
                title={
                  windowType === BookingFormType.view
                    ? 'Дата заезда'
                    : 'Дата заезда*'
                }
              />
              <Calendar
                name="check_in_date"
                id="check_in_date"
                disabled={windowType === BookingFormType.view ? true : false}
                placeholderText="Выберите дату заезда"
                selectedDate={values.check_in_date}
                onChange={(val) => {
                  setFieldValue('check_in_date', val);
                }}
                isSmall={true}
                minDate={
                  windowType === BookingFormType.edit ? startCalendar : today
                }
                maxDate={endCalendarForCheckIn}
                isError={!!errors.check_in_date}
                onBlur={() => setFieldTouched('check_in_date')}
                errText={errors.check_in_date}
              />
            </div>

            <div className={styles.out}>
              <BookingLineTitle
                title={
                  windowType === BookingFormType.view
                    ? 'Дата выезда'
                    : 'Дата выезда*'
                }
              />
              <Calendar
                name="check_out_date"
                id="check_out_date"
                disabled={windowType === BookingFormType.view ? true : false}
                placeholderText="Выберите дату выезда"
                selectedDate={values.check_out_date}
                onChange={(val) => {
                  setFieldValue('check_out_date', val);
                }}
                isSmall={true}
                minDate={
                  (values.check_in_date && addDays(values.check_in_date, 1)) ||
                  addDays(today, 1)
                }
                maxDate={endCalendar}
                isError={!!errors.check_out_date}
                onBlur={() => setFieldTouched('check_out_date')}
                errText={errors.check_out_date}
              />
            </div>
          </div>
          <div
            className={classNames(
              styles.column,
              styles.dateBlock,
              windowType !== BookingFormType.view && styles.edit,
            )}
          >
            <div>
              <BookingLineTitle
                title={
                  windowType === BookingFormType.view
                    ? 'Время заезда'
                    : 'Время заезда*'
                }
              />
              {windowType === BookingFormType.view && (
                <div className={styles.viewTime}>
                  {values.check_in_time && 'c ' + values.check_in_time}
                </div>
              )}
              {windowType !== BookingFormType.view && (
                <SelectComponentV2
                  title={values.check_in_time ? values.check_in_time : '-'}
                  placeholder=""
                  currentValue={values.check_in_time}
                  setSelecValue={handleChange('check_in_time')}
                  optionsList={timeList}
                  onBlur={() => setFieldTouched('check_in_time')}
                  errText={
                    errors.check_in_time ===
                    'Бронь должна быть длительностью не менее 12 часов'
                      ? 'no-text'
                      : errors.check_in_time
                  }
                  isBooking
                  isTime
                />
              )}
            </div>
            <div className={styles.out}>
              <BookingLineTitle
                title={
                  windowType === BookingFormType.view
                    ? 'Время выезда'
                    : 'Время выезда*'
                }
              />
              {windowType === BookingFormType.view && (
                <div className={styles.viewTime}>
                  {values.check_out_time && 'до ' + values.check_out_time}
                </div>
              )}
              {windowType !== BookingFormType.view && (
                <SelectComponentV2
                  title={values.check_out_time ? values.check_out_time : '-'}
                  placeholder=""
                  currentValue={values.check_out_time}
                  setSelecValue={handleChange('check_out_time')}
                  optionsList={timeList}
                  onBlur={() => setFieldTouched('check_out_time')}
                  errText={errors.check_out_time}
                  isBooking
                  isTime
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.column}>
          <PersonCount
            adult={values.adult || 1}
            children={values.children}
            windowType={windowType}
            setAdult={(val) => {
              setFieldValue('adult', val);
            }}
            setKids={(val) => {
              setFieldValue('children', val);
            }}
          />
          {errors.adult && <div className={styles.error}>{errors.adult}</div>}
        </div>
        <div className={styles.animalBlock}>
          <div className={styles.animalLine}>
            <span
              className={classNames(
                styles.animalTitle,
                windowType === BookingFormType.view && styles.disabled,
              )}
            >
              Есть ли животные? {values.has_animals && `*`}
            </span>

            {windowType !== BookingFormType.view && (
              <div className={styles.checkbox}>
                <Checkbox
                  checked={values.has_animals}
                  label=""
                  name="has_animals"
                  onClick={() => {
                    setFieldValue('has_animals', !values.has_animals);
                  }}
                />
              </div>
            )}
          </div>
          {windowType === BookingFormType.view && (
            <>
              {Number(values.animals_count)>0 && 
              <div className={styles.animalInfo}>
                Количество: {values.animals_count}
              </div>}
              <div className={styles.animalInfo}>{animalInfoView}</div>
            </>
          )}
          {windowType !== BookingFormType.view && formik.values.has_animals && (
            <div>
              <div className={classNames(styles.column, styles.animal_count)}>
                <Input
                  id="animals_count"
                  name="animals_count"
                  title="Количество*"
                  type={InputType.MASK}
                  value={values.animals_count || ''}
                  placeholder="0"
                  onChange={handleChange('animals_count')}
                  isBooking={true}
                  onBlur={() => setFieldTouched('animals_count')}
                  errText={errors.animals_count}
                  mask={{
                    mask: /^(?!0)\d{0,2}$/,
                    thousandsSeparator: '',
                    min: 1,
                    max: 99,
                    scale: 0,
                  }}
                />
              </div>
              <Input
                id="animals_info"
                name="animals_info"
                title="Краткое описание*"
                type={InputType.TEXTAREA}
                value={values.animals_info}
                placeholder="Вид животного, описание"
                onChange={handleChange('animals_info')}
                isBooking={true}
                onBlur={() => setFieldTouched('animals_info')}
                errText={errors.animals_info}
                maxLength={100}
                requirementsText={ANIMALINFO_REQ}
              />
            </div>
          )}
        </div>
        <PriceBlock windowType={windowType} formik={formik} />
      </div>
      <BookingSectionTitle title="Комментарий владельца" />
      <div className={styles.container}>
        {windowType === BookingFormType.view && (
          <div className={styles.commentView}>{commentView}</div>
        )}
        {windowType !== BookingFormType.view && (
          <Input
            title="Комментарий владельца"
            name="owner_comment"
            id="owner_comment"
            type={InputType.TEXTAREA}
            value={values.owner_comment}
            placeholder="Введите текст"
            onChange={handleChange('owner_comment')}
            isBooking={true}
            className={'comment'}
            onBlur={() => setFieldTouched('owner_comment')}
            errText={errors.owner_comment}
            requirementsText={COMMENT_REQ}
            maxLength={550}
          />
        )}
      </div>
    </div>
  );
};

export default BookingObjectBlock;
